import { parseJwt } from '@realadvisor/auth-client';

// Durable, cross-tab preference. Only `true` is acted on: it is what keeps the
// panel open for someone who opted in on a viewport too narrow to auto-open.
const STORAGE_KEY_OPEN = 'chat-panel-open';

// Tab-scoped dismissal. `sessionStorage` is per browsing context, so closing
// the panel silences it for this tab and its reloads only — a new tab starts
// over and auto-opens again. Deliberate: the auto-open is a recurring nudge
// during the ai_chat rollout, not a one-shot the first close ends forever.
const SESSION_KEY_CLOSED = 'chat-panel-closed';

// Conversation id for this tab, keyed by user: "Log in as" and logout/login
// reload the same tab, and the chat API refuses a chat id owned by someone
// else. No user id means no persistence, never a key shared across users —
// which is also why the old unscoped `ra-chat-id` key is not read.
const SESSION_CHAT_KEY_PREFIX = 'ra-chat-id';

// `user.id` is the field crm-chat-api compares with the chat's owner; while
// impersonating it is the impersonated user.
export const readChatSessionUserId = (): string => {
  try {
    const token = localStorage.getItem('access_token');
    if (token == null) {
      return '';
    }
    const userId = parseJwt(token)?.user?.id;
    return typeof userId === 'string' ? userId : '';
  } catch {
    return '';
  }
};

export const chatSessionKey = (userId: string): string | null =>
  userId === '' ? null : `${SESSION_CHAT_KEY_PREFIX}:${userId}`;

export const readSessionChatId = (userId: string): string | null => {
  const key = chatSessionKey(userId);
  if (key == null) {
    return null;
  }
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

export const writeSessionChatId = (userId: string, chatId: string): void => {
  const key = chatSessionKey(userId);
  if (key == null) {
    return;
  }
  try {
    sessionStorage.setItem(key, chatId);
  } catch {
    /* private mode or storage full — keep the in-memory id only */
  }
};

export const MOBILE_CHAT_BREAKPOINT = 767;

// The panel opens at 25% of the split, on top of the sidebar, so below a
// desktop-sized viewport it leaves the grids and kanbans too narrow to be
// worth opening unasked. Same 80em as `MEDIA_DESKTOP` in shared/theme/layout.
export const AUTO_OPEN_MIN_WIDTH = 1280;

// `null` when the user has never toggled the panel on this browser.
export const readStoredChatPanelOpen = (): boolean | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_OPEN);
    return stored == null ? null : stored === 'true';
  } catch {
    return null;
  }
};

export const readChatPanelClosedInThisTab = (): boolean => {
  try {
    return sessionStorage.getItem(SESSION_KEY_CLOSED) === 'true';
  } catch {
    return false;
  }
};

export const writeChatPanelPreference = (open: boolean): void => {
  try {
    localStorage.setItem(STORAGE_KEY_OPEN, String(open));
  } catch {
    /* private mode or storage full — keep the in-memory state only */
  }
  try {
    if (open) {
      sessionStorage.removeItem(SESSION_KEY_CLOSED);
    } else {
      sessionStorage.setItem(SESSION_KEY_CLOSED, 'true');
    }
  } catch {
    /* empty */
  }
};

export const resolveInitialChatPanelOpen = ({
  viewportWidth,
  storedPreference,
  closedInThisTab,
  canAccessChat,
}: {
  viewportWidth: number;
  storedPreference: boolean | null;
  closedInThisTab: boolean;
  canAccessChat: boolean;
}): boolean => {
  if (viewportWidth <= MOBILE_CHAT_BREAKPOINT || !canAccessChat) {
    return false;
  }
  if (closedInThisTab) {
    return false;
  }
  if (storedPreference === true) {
    return true;
  }
  // A stored `false` deliberately does not land here: an explicit close is
  // scoped to the tab it happened in, so the next tab nudges again.
  return viewportWidth >= AUTO_OPEN_MIN_WIDTH;
};
